/**
 * Единый логгер мини-аппа (Telegram + MAX).
 *
 * В мини-аппе на телефоне нет доступа к консоли браузера, поэтому:
 *  - все записи копятся в кольцевой буфер (window.nmLogs() — прочитать,
 *    window.nmDumpLogs() — получить текстом, чтобы переслать);
 *  - по ?debug=1 поднимается eruda — консоль прямо на экране телефона;
 *  - глобальные ошибки и необработанные промисы пишутся автоматически.
 *
 * Секреты (токены, телефоны) маскируются перед записью — logcat/консоль
 * читаемы с устройства.
 */

const MAX_BUFFER = 300;
const MAX_DEPTH = 3;
const MAX_KEYS = 30;
const MAX_ARRAY = 20;

const SECRET_KEY = /token|secret|password|phone|auth/i;

const buffer = [];

const maskString = (value) => {
    if (typeof value !== "string" || !value) return value;
    if (value.length <= 8) return "***";
    return `${value.slice(0, 4)}…${value.slice(-2)}`;
};

/** Делает значение безопасным для лога: маскирует секреты, режет глубину и циклы. */
const safeValue = (value, depth = 0) => {
    try {
        if (value === null || value === undefined) return value;
        if (typeof value !== "object") return value;
        if (depth >= MAX_DEPTH) return "…";
        if (value instanceof Error) {
            return { name: value.name, message: value.message, stack: (value.stack || "").slice(0, 400) };
        }
        if (Array.isArray(value)) {
            return value.slice(0, MAX_ARRAY).map((item) => safeValue(item, depth + 1));
        }
        const result = {};
        for (const key of Object.keys(value).slice(0, MAX_KEYS)) {
            const raw = value[key];
            result[key] = SECRET_KEY.test(key) && typeof raw === "string"
                ? maskString(raw)
                : safeValue(raw, depth + 1);
        }
        return result;
    } catch (error) {
        return "[unserializable]";
    }
};

const CONSOLE_METHOD = { debug: "log", info: "log", warn: "warn", error: "error" };

const write = (level, scope, message, data) => {
    const entry = {
        time: new Date().toISOString(),
        level,
        scope,
        message,
        data: data === undefined ? undefined : safeValue(data),
    };

    buffer.push(entry);
    if (buffer.length > MAX_BUFFER) buffer.shift();

    const prefix = `[NM][${scope}]`;
    const method = CONSOLE_METHOD[level] || "log";
    try {
        if (entry.data === undefined) console[method](prefix, message);
        else console[method](prefix, message, entry.data);
    } catch (error) {
        /* консоль может быть недоступна — запись в буфер уже сделана */
    }

    return entry;
};

/** Логгер со скоупом фичи: createLogger("active-order").info("...", {...}) */
export const createLogger = (scope) => ({
    debug: (message, data) => write("debug", scope, message, data),
    info: (message, data) => write("info", scope, message, data),
    warn: (message, data) => write("warn", scope, message, data),
    error: (message, data) => write("error", scope, message, data),
});

export const logger = createLogger("app");

export const getLogs = () => buffer.slice();

export const dumpLogs = () => buffer
    .map((entry) => {
        const data = entry.data === undefined ? "" : ` ${JSON.stringify(entry.data)}`;
        return `${entry.time} ${entry.level.toUpperCase()} [${entry.scope}] ${entry.message}${data}`;
    })
    .join("\n");

const isDebugRequested = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get("debug") === "1") {
            try {
                localStorage.setItem("nm_debug", "1");
            } catch (error) {
                /* приватный режим — флаг просто не запомнится */
            }
            return true;
        }
        if (params.get("debug") === "0") {
            try {
                localStorage.removeItem("nm_debug");
            } catch (error) { /* игнорируем */ }
            return false;
        }
        return localStorage.getItem("nm_debug") === "1";
    } catch (error) {
        return false;
    }
};

/**
 * Ставит глобальные обработчики ошибок и, если запрошено ?debug=1,
 * подгружает eruda (динамический импорт — в основной бандл не попадает).
 */
export const initLogging = () => {
    try {
        window.nmLogs = getLogs;
        window.nmDumpLogs = dumpLogs;
    } catch (error) { /* игнорируем */ }

    window.addEventListener("error", (event) => {
        write("error", "window", "Необработанная ошибка", {
            message: event.message,
            source: event.filename,
            line: event.lineno,
        });
    });

    window.addEventListener("unhandledrejection", (event) => {
        write("error", "window", "Необработанный промис", { reason: event.reason });
    });

    if (!isDebugRequested()) return;

    import("eruda")
        .then((module) => {
            const eruda = module.default || module;
            eruda.init();
            write("info", "debug", "eruda-консоль включена (?debug=1)");
        })
        .catch((error) => {
            write("warn", "debug", "eruda не загрузилась", error);
        });
};
